import React, { useState } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { Badge } from '../../../design-system/Badge';
import { Button } from '../../../design-system/Button';
import { Card } from '../../../design-system/Card';
import { Modal } from '../../../design-system/Modal';
import { Input, Textarea } from '../../../design-system/Input';
import {
  Flag,
  Plus,
  Link2
} from 'lucide-react';

export const TimelineView = () => {
  const { tasks = [], setSelectedTask, setIsQuickAddOpen } = useWorkspace();
  const [zoomLevel, setZoomLevel] = useState('days'); // 'days', 'weeks', 'months'
  const [activeTab, setActiveTab] = useState('timeline'); // 'timeline', 'dependencies'
  const [isNewMilestoneModalOpen, setIsNewMilestoneModalOpen] = useState(false);

  const [milestones, setMilestones] = useState([
    { id: 'm1', title: 'Design System & Neomorphic Baseline', date: '2026-08-22', status: 'done', progress: 100 },
    { id: 'm2', title: 'Sprint 1 Acceptance Review & Demo', date: '2026-08-28', status: 'pending', progress: 70 },
    { id: 'm3', title: 'Public Beta Launch & Ecosystem', date: '2026-09-15', status: 'pending', progress: 25 }
  ]);

  const [newMilestoneTitle, setNewMilestoneTitle] = useState('');
  const [newMilestoneDate, setNewMilestoneDate] = useState('2026-08-30');
  const [newMilestoneDesc, setNewMilestoneDesc] = useState('');

  // Date Axis for 14 Days
  const dateColumns = [
    { dayNum: 18, label: 'Aug 18', isToday: false },
    { dayNum: 19, label: 'Aug 19', isToday: false },
    { dayNum: 20, label: 'Aug 20', isToday: false },
    { dayNum: 21, label: 'Aug 21', isToday: false },
    { dayNum: 22, label: 'Aug 22 (Today)', isToday: true },
    { dayNum: 23, label: 'Aug 23', isToday: false },
    { dayNum: 24, label: 'Aug 24', isToday: false },
    { dayNum: 25, label: 'Aug 25', isToday: false },
    { dayNum: 26, label: 'Aug 26', isToday: false },
    { dayNum: 27, label: 'Aug 27', isToday: false },
    { dayNum: 28, label: 'Aug 28', isToday: false },
    { dayNum: 29, label: 'Aug 29', isToday: false },
    { dayNum: 30, label: 'Aug 30', isToday: false },
    { dayNum: 31, label: 'Aug 31', isToday: false }
  ];

  const handleAddMilestone = (e) => {
    e?.preventDefault();
    if (!newMilestoneTitle.trim()) return;
    const newM = {
      id: 'm_' + Date.now(),
      title: newMilestoneTitle.trim(),
      date: newMilestoneDate,
      status: 'pending',
      progress: 0
    };
    setMilestones([...milestones, newM]);
    setNewMilestoneTitle('');
    setNewMilestoneDesc('');
    setIsNewMilestoneModalOpen(false);
  };

  const getTaskColor = (priority, status) => {
    if (status === 'done') return 'var(--color-success)';
    if (priority === 'urgent') return 'var(--color-danger)';
    if (priority === 'high') return 'var(--color-warning)';
    return 'var(--accent-teal)';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Top Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Gantt Timeline & Milestones</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Schedule roadmaps, dependency sequencing, and milestone deliverables
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* View Toggle */}
          <div style={{ display: 'flex', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-sm)', padding: '2px' }}>
            <button
              onClick={() => setActiveTab('timeline')}
              style={{
                padding: '5px 10px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: activeTab === 'timeline' ? 'var(--surface-card)' : 'transparent',
                boxShadow: activeTab === 'timeline' ? 'var(--shadow-raised-sm)' : 'none',
                border: 'none',
                color: activeTab === 'timeline' ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Gantt Roadmap
            </button>
            <button
              onClick={() => setActiveTab('dependencies')}
              style={{
                padding: '5px 10px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: activeTab === 'dependencies' ? 'var(--surface-card)' : 'transparent',
                boxShadow: activeTab === 'dependencies' ? 'var(--shadow-raised-sm)' : 'none',
                border: 'none',
                color: activeTab === 'dependencies' ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Dependency Graph
            </button>
          </div>

          {/* Zoom Selector */}
          <div style={{ display: 'flex', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-sm)', padding: '2px' }}>
            {['days', 'weeks', 'months'].map((z) => (
              <button
                key={z}
                onClick={() => setZoomLevel(z)}
                style={{
                  padding: '5px 8px',
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: zoomLevel === z ? 'var(--surface-card)' : 'transparent',
                  border: 'none',
                  color: zoomLevel === z ? 'var(--accent-teal)' : 'var(--text-muted)',
                  fontSize: '11px',
                  fontWeight: '700',
                  textTransform: 'capitalize',
                  cursor: 'pointer'
                }}
              >
                {z}
              </button>
            ))}
          </div>

          <Button
            variant="secondary"
            size="sm"
            icon={<Flag size={14} color="var(--color-purple)" />}
            onClick={() => setIsNewMilestoneModalOpen(true)}
          >
            New Milestone
          </Button>

          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => setIsQuickAddOpen(true)}
          >
            New Task
          </Button>
        </div>
      </div>

      {/* MILESTONES SUMMARY BAR */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
        {milestones.map((m) => (
          <div
            key={m.id}
            style={{
              padding: '14px 16px',
              backgroundColor: 'var(--surface-card)',
              boxShadow: 'var(--shadow-raised-sm)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-subtle)',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                <Flag size={15} color="var(--color-purple)" />
                <span>{m.title}</span>
              </div>
              <Badge variant={m.status === 'done' ? 'success' : 'purple'} size="xs">
                {m.status === 'done' ? 'Achieved' : m.date}
              </Badge>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)' }}>
              <span>Progress</span>
              <span style={{ fontWeight: '700', color: 'var(--accent-teal)' }}>{m.progress}%</span>
            </div>
            <div style={{ height: '4px', backgroundColor: 'var(--surface-recessed)', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${m.progress}%`, backgroundColor: m.status === 'done' ? 'var(--color-success)' : 'var(--color-purple)', borderRadius: '999px' }} />
            </div>
          </div>
        ))}
      </div>

      {/* MAIN TIMELINE ROADMAP VIEW */}
      {activeTab === 'timeline' ? (
        <Card padding="20px">
          {/* Timeline Grid Table */}
          <div style={{ overflowX: 'auto', position: 'relative' }}>
            {/* Header Date Axis */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '260px repeat(14, minmax(70px, 1fr))',
                borderBottom: '1px solid var(--border-divider)',
                paddingBottom: '12px',
                fontSize: '11px',
                fontWeight: '700',
                color: 'var(--text-muted)',
                letterSpacing: '0.04em',
                textAlign: 'center',
                userSelect: 'none'
              }}
            >
              <div style={{ textAlign: 'left', paddingLeft: '8px' }}>Task / Milestone</div>
              {dateColumns.map((col) => (
                <div
                  key={col.dayNum}
                  style={{
                    color: col.isToday ? 'var(--accent-teal)' : 'var(--text-muted)',
                    fontWeight: col.isToday ? '800' : '700'
                  }}
                >
                  {col.label}
                </div>
              ))}
            </div>

            {/* Task Rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
              {tasks.map((task, idx) => {
                const isBlocked = (task.blockedBy || []).length > 0;
                // Calculate pseudo horizontal offset and span for visual demo
                const leftPercent = 10 + (idx % 5) * 16;
                const widthPercent = 25 + (idx % 3) * 10;

                return (
                  <div
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '260px 1fr',
                      alignItems: 'center',
                      padding: '6px 0',
                      cursor: 'pointer',
                      borderRadius: 'var(--radius-sm)'
                    }}
                    className="interactive-raised"
                  >
                    {/* Left Task Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingRight: '12px', overflow: 'hidden' }}>
                      <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)' }}>
                        {task.id}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {task.title}
                      </span>
                    </div>

                    {/* Gantt Bar Track */}
                    <div
                      style={{
                        position: 'relative',
                        width: '100%',
                        height: '28px',
                        backgroundColor: 'var(--surface-recessed)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-subtle)'
                      }}
                    >
                      {/* Bar Fill */}
                      <div
                        style={{
                          position: 'absolute',
                          left: `${leftPercent}%`,
                          width: `${widthPercent}%`,
                          height: '100%',
                          backgroundColor: getTaskColor(task.priority, task.status),
                          borderRadius: 'var(--radius-sm)',
                          boxShadow: 'var(--shadow-raised-sm)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0 8px',
                          color: '#ffffff',
                          fontSize: '11px',
                          fontWeight: '700'
                        }}
                      >
                        <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {task.title}
                        </span>
                        {isBlocked && (
                          <span style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '1px 4px', borderRadius: '3px', fontSize: '9px' }}>
                            BLOCKED
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      ) : (
        /* DEPENDENCY GRAPH VIEW */
        <Card padding="24px">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Link2 size={18} color="var(--accent-teal)" />
            <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Task Dependency Chains & Cycles</h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {tasks
              .filter((t) => (t.blockedBy || []).length > 0 || (t.blocking || []).length > 0)
              .map((t) => (
                <div
                  key={t.id}
                  style={{
                    padding: '14px 18px',
                    backgroundColor: 'var(--surface-recessed)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '800', color: 'var(--accent-teal)' }}>{t.id}</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{t.title}</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {(t.blockedBy || []).length > 0 && (
                      <Badge variant="danger" size="xs">
                        Waiting on: {t.blockedBy.join(', ')}
                      </Badge>
                    )}
                    {(t.blocking || []).length > 0 && (
                      <Badge variant="purple" size="xs">
                        Blocks: {t.blocking.join(', ')}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </Card>
      )}

      {/* NEW MILESTONE MODAL */}
      <Modal
        isOpen={isNewMilestoneModalOpen}
        onClose={() => setIsNewMilestoneModalOpen(false)}
        title="Create Target Milestone"
        subtitle="Define a critical delivery date and attach milestone goals"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsNewMilestoneModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddMilestone} icon={<Flag size={14} />}>
              Create Milestone
            </Button>
          </>
        }
      >
        <form onSubmit={handleAddMilestone} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Milestone Title *"
            placeholder="e.g., Alpha Release Candidate 1"
            value={newMilestoneTitle}
            onChange={(e) => setNewMilestoneTitle(e.target.value)}
            required
            autoFocus
          />

          <Input
            label="Target Delivery Date *"
            type="date"
            value={newMilestoneDate}
            onChange={(e) => setNewMilestoneDate(e.target.value)}
            required
          />

          <Textarea
            label="Description & Acceptance Goals"
            placeholder="Key success metrics and acceptance criteria for this milestone..."
            value={newMilestoneDesc}
            onChange={(e) => setNewMilestoneDesc(e.target.value)}
            rows={3}
          />
        </form>
      </Modal>
    </div>
  );
};
